import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLang } from '@/utils/i18n';
import { ArrowLeft, MessageCircle, Send, Mail, MailOpen, ChevronRight } from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

const MessagesPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [inbox, setInbox] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadInbox(); }, []);

  const loadInbox = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/social/messages/inbox`, { headers });
      if (res.ok) {
        const data = await res.json();
        setInbox(data.messages || []);
        setUnread(data.unread || 0);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const openConversation = async (userId, userName) => {
    setConversation({ userId, userName });
    try {
      const res = await fetch(`${BACKEND_URL}/api/social/messages/conversation/${userId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch { /* silent */ }
    loadInbox(); // Refresh unread count
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !conversation) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/social/messages/send`, {
        method: 'POST', headers,
        body: JSON.stringify({ to_user_id: conversation.userId, text: newMsg.trim() }),
      });
      if (res.ok) {
        setNewMsg('');
        openConversation(conversation.userId, conversation.userName);
      }
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  // Group inbox by unique sender
  const uniqueSenders = {};
  inbox.forEach((m) => {
    const key = m.from_id;
    if (!uniqueSenders[key] || m.sent_at > uniqueSenders[key].sent_at) {
      uniqueSenders[key] = m;
    }
  });
  const senderList = Object.values(uniqueSenders).sort((a, b) => b.sent_at.localeCompare(a.sent_at));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="messages-page">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button
            onClick={() => conversation ? setConversation(null) : navigate('/dashboard')}
            variant="ghost" className="text-gray-400 hover:text-white"
            data-testid="messages-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {conversation ? conversation.userName : t('back')}
          </Button>
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#00BFA6]" />
            {conversation ? '' : 'Nachrichten'}
            {!conversation && unread > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{unread}</span>
            )}
          </h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {!conversation ? (
          /* Inbox */
          <div className="space-y-2" data-testid="inbox-list">
            {senderList.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{lang === 'de' ? 'Keine Nachrichten' : 'No messages'}</p>
                <p className="text-xs mt-1 text-gray-600">
                  {lang === 'de' ? 'Schreibe einem Tipster auf der Tip-Sharing Seite' : 'Message a tipster from the Tip Sharing page'}
                </p>
              </div>
            ) : (
              senderList.map((m, i) => (
                <button
                  key={i}
                  data-testid={`inbox-item-${i}`}
                  onClick={() => openConversation(m.from_id, m.from_name)}
                  className={`w-full text-left p-4 rounded-xl transition-colors flex items-center gap-3 ${
                    !m.read ? 'bg-[#00BFA6]/5 border border-[#00BFA6]/20' : 'bg-[#1E1E1E] border border-white/5'
                  } hover:bg-white/5`}
                >
                  {!m.read ? <Mail className="w-5 h-5 text-[#00BFA6] shrink-0" /> : <MailOpen className="w-5 h-5 text-gray-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-sm font-medium ${!m.read ? 'text-white' : 'text-gray-300'}`}>{m.from_name}</span>
                      <span className="text-[10px] text-gray-500">{timeAgo(m.sent_at)}</span>
                    </div>
                    <p className="text-xs text-gray-400 truncate">{m.text}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
                </button>
              ))
            )}
          </div>
        ) : (
          /* Conversation */
          <div data-testid="conversation-view">
            <div className="space-y-3 mb-4 max-h-[60vh] overflow-y-auto pr-1">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Konversation starten...</div>
              ) : (
                messages.map((m, i) => {
                  const isMe = m.from_id !== conversation.userId;
                  return (
                    <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                        isMe ? 'bg-[#00BFA6]/20 text-white' : 'bg-white/5 text-gray-200'
                      }`}>
                        <p className="text-sm">{m.text}</p>
                        <span className="text-[10px] text-gray-500 mt-1 block">{timeAgo(m.sent_at)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className="flex gap-2 sticky bottom-16 md:bottom-4 bg-[#121212] py-2">
              <input
                data-testid="message-input"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={lang === 'de' ? 'Nachricht schreiben...' : 'Type a message...'}
                maxLength={500}
                className="flex-1 px-4 py-3 bg-[#1E1E1E] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-500 focus:border-[#00BFA6] focus:outline-none"
              />
              <Button
                data-testid="send-message-btn"
                onClick={sendMessage}
                disabled={sending || !newMsg.trim()}
                className="bg-[#00BFA6] hover:bg-[#009E8A] text-black px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default MessagesPage;
